import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import type {
  ClipboardEventHandler,
  CSSProperties,
  FocusEventHandler,
  FormEventHandler,
  KeyboardEventHandler,
  MouseEvent,
  ReactEventHandler,
  ReactNode,
  Ref,
} from 'react'
import { CheckIcon, ChevronDownIcon, CopyIcon, InfoIcon } from '../icons/icons'
import { MultiValueSelector, type MultiValueSelectorOption } from './MultiValueSelector'
import { ValueSelector } from './ValueSelector'
import type { StatusTone } from './pageHeaderStatusTones'
import { Tooltip } from './Tooltip'
import { IconButton } from './IconButton'
import EmailListInput from './EmailListInput'
import DateListInput from './DateListInput'
import { useFieldDensity } from './fieldDensity'
import { useClipboardCopy } from '../hooks/useClipboardCopy'
import { ToggleSwitch } from './ToggleSwitch'
import { getResizeCursor, type ResizeAxes, type ResizeCorner } from '../utils/resizeCursor'

import {
  REQUIREMENT_MARK_LABEL,
  useRequirementMark,
  type RequirementIndicatorOverride,
} from './requirementMarks'
interface FieldInputBaseProps {
  label: string
  /** For controls inside a `FieldRow`, whose shared label names the row. */
  hideLabel?: boolean
  labelTooltip?: ReactNode
  onBlur?: () => void
  placeholder?: string
  className?: string
  readOnly?: boolean
  required?: boolean
  formMode?: 'create' | 'update'
  /**
   * Its own say on its requirement mark: `show` marks it (Required or
   * Optional) whatever its form decides, `hide` never. By default its form
   * (FormRequirements) marks only the less common kind.
   */
  requirementIndicator?: RequirementIndicatorOverride
  error?: boolean
  errorMessage?: string
  /** Ids of text that explains the field; the error message is added on its own. */
  describedBy?: string
  controlClassName?: string
  labelClassName?: string
  statusIndicator?: {
    label: string
    className: string
    icon?: React.ReactNode
    /** After the label, such as a chevron that opens the status's details. */
    trailing?: React.ReactNode
  } | null
  endActions?: ReactNode
  endActionsVisible?: boolean
  /** Controls that act on the whole field, right after its label (the formula's undo history). */
  labelAccessory?: ReactNode
  /** Actions at the end of the label row, before the status (the formula's Write with AI). */
  labelActions?: ReactNode
}

interface StringFieldInputProps extends FieldInputBaseProps {
  value: string
  onChange: (value: string) => void
}

interface ShortTextFieldInputProps extends StringFieldInputProps {
  type: 'short-text'
  /** Rewrites the text as it is typed, keeping the cursor in place. */
  normalize?: (value: string) => string
  /** A password or other secret: masked, and never filled in by the browser. */
  secret?: boolean
}

interface NativeDateTimeFieldInputProps extends StringFieldInputProps {
  type: 'date' | 'time' | 'datetime-local'
  min?: string
  max?: string
  step?: number
}

interface StepperFieldInputProps extends StringFieldInputProps {
  type: 'stepper'
  /** Increment applied by the arrows and snapped to on blur. Defaults to 1. */
  step?: number
  min?: number
  max?: number
  /** Unit hint rendered inside the control beside the arrows (e.g. "hr"). */
  suffix?: string
}

interface LongTextFieldInputProps extends StringFieldInputProps {
  type: 'long-text'
  resizeMode?: 'native' | 'custom' | 'none'
  style?: CSSProperties
  controlRef?: Ref<HTMLTextAreaElement>
  onControlFocus?: FocusEventHandler<HTMLTextAreaElement>
  onControlBeforeInput?: FormEventHandler<HTMLTextAreaElement>
  onControlKeyDown?: KeyboardEventHandler<HTMLTextAreaElement>
  onControlSelect?: ReactEventHandler<HTMLTextAreaElement>
  onControlPaste?: ClipboardEventHandler<HTMLTextAreaElement>
  onResizeStart?: (event: MouseEvent<HTMLDivElement>, corner: ResizeCorner) => void
  /** Axes actually changed by the custom resize handler. Defaults to both. */
  resizeAxes?: ResizeAxes
  /**
   * Part of the same control, below the text and inside its border (the
   * formula editor's function help and add buttons). The control's focus
   * border then goes around both, and the label row becomes its top band.
   */
  controlFooter?: ReactNode
  /** With a footer: inside the box, under its label band and above the text (the formula's AI writer). */
  controlHeader?: ReactNode
}

interface SelectFieldInputProps extends StringFieldInputProps {
  type: 'select'
  options: {
    value: string
    label: string
    description?: string
    tone?: StatusTone
    /** A stand-in choice ("No specific module"), in italics like the app's others. */
    italic?: boolean
    disabled?: boolean
    disabledReason?: string
  }[]
  /**
   * Whether the list offers an empty choice. Defaults to "when not required";
   * pass `false` for a value that can never be empty while not yet required.
   */
  allowEmpty?: boolean
  clearable?: boolean
  emptyLabel?: string
  /** Explicit search control; defaults to the selector's option-count threshold. */
  showSearch?: boolean
  /**
   * Opens its list, which takes the keyboard, each time it goes up: for a
   * form that starts on this question (Contact Support's first).
   */
  openRequest?: number
}

interface MultiSelectFieldInputProps extends FieldInputBaseProps {
  type: 'multi-select'
  value: string[]
  onChange: (value: string[]) => void
  options: Array<Omit<MultiValueSelectorOption, 'id'> & { value: string }>
  /** Explicit search control; defaults to the selector's option-count threshold. */
  showSearch?: boolean
  /** Chip remove-button label; defaults to the permission-domain wording. */
  chipRemoveLabel?: (label: string) => string
  /** Tooltip on directly selected chips. */
  chipTitle?: string
  onOpen?: () => void
  ordered?: boolean
}

interface EmailListFieldInputProps extends FieldInputBaseProps {
  type: 'email-list'
  value: string[]
  onChange: (value: string[]) => void
  /** Valid addresses typed but not yet added (see `EmailListInput`). */
  onPendingChange?: (emails: string[]) => void
}

interface BooleanFieldInputProps extends FieldInputBaseProps {
  type: 'boolean'
  value: boolean
  onChange: (value: boolean) => void
  /** Place the label inside the control instead of a header and Yes/No value. */
  inlineLabel?: boolean
}

interface DateListFieldInputProps extends FieldInputBaseProps {
  type: 'date-list'
  /** ISO dates (YYYY-MM-DD). */
  value: string[]
  onChange: (value: string[]) => void
}

type FieldInputProps =
  | ShortTextFieldInputProps
  | StepperFieldInputProps
  | NativeDateTimeFieldInputProps
  | LongTextFieldInputProps
  | SelectFieldInputProps
  | MultiSelectFieldInputProps
  | EmailListFieldInputProps
  | DateListFieldInputProps
  | BooleanFieldInputProps

interface InternalMetadataFieldProps {
  label: string
  value: string
  tooltip: React.ReactNode
  className?: string
}

/**
 * Rounds a typed value to the nearest step within the bounds. Text that is not
 * a number is returned unchanged so validation can surface it.
 */
function snapStepperValue(
  raw: string,
  opts: { step?: number; min?: number; max?: number }
): string {
  const step = opts.step ?? 1
  const parsed = Number(raw.trim())
  if (!Number.isFinite(parsed) || raw.trim() === '') return raw
  let value = Math.round(parsed / step) * step
  if (opts.min !== undefined) value = Math.max(opts.min, value)
  if (opts.max !== undefined) value = Math.min(opts.max, value)
  return String(Math.round(value * 10000) / 10000)
}

function stepStepperValue(
  raw: string,
  opts: { step?: number; min?: number; max?: number },
  direction: 1 | -1
): string {
  const step = opts.step ?? 1
  const parsed = Number(raw.trim())
  const base = Number.isFinite(parsed) && raw.trim() !== '' ? parsed : (opts.min ?? 0)
  return snapStepperValue(String(base + direction * step), opts)
}

function ResizeGlyph({ corner }: { corner: ResizeCorner }) {
  const dotClass = 'absolute h-0.5 w-0.5 rounded-full bg-[color:var(--border-strong)]'
  const dots =
    corner === 'right'
      ? [
          ['right-[4px]', 'bottom-[12px]'],
          ['right-[4px]', 'bottom-[8px]'],
          ['right-[8px]', 'bottom-[8px]'],
          ['right-[4px]', 'bottom-[4px]'],
          ['right-[8px]', 'bottom-[4px]'],
          ['right-[12px]', 'bottom-[4px]'],
        ]
      : [
          ['left-[4px]', 'bottom-[12px]'],
          ['left-[4px]', 'bottom-[8px]'],
          ['left-[8px]', 'bottom-[8px]'],
          ['left-[4px]', 'bottom-[4px]'],
          ['left-[8px]', 'bottom-[4px]'],
          ['left-[12px]', 'bottom-[4px]'],
        ]

  return (
    <div className="pointer-events-none absolute inset-0">
      {dots.map(([xClass, yClass]) => (
        <span key={`${xClass}-${yClass}`} className={`${dotClass} ${xClass} ${yClass}`} />
      ))}
    </div>
  )
}

export function FieldInput(props: FieldInputProps) {
  const { label, className = '', controlClassName = '' } = props
  const textInputRef = useRef<HTMLInputElement | null>(null)
  // A new object each time, so the cursor is placed even when the text comes out the same.
  const [normalizedCursor, setNormalizedCursor] = useState<{ at: number } | null>(null)
  useLayoutEffect(() => {
    if (normalizedCursor === null) return
    textInputRef.current?.setSelectionRange(normalizedCursor.at, normalizedCursor.at)
  }, [normalizedCursor])
  const compact = useFieldDensity() === 'compact'
  const errorId = useId()
  const helpId = useId()
  const copyErrorId = useId()
  const { copied, copyError, isCopying, copy } = useClipboardCopy()
  const showsError = Boolean(props.error && props.errorMessage)
  const describedBy =
    [props.describedBy, showsError ? errorId : null, copyError ? copyErrorId : null]
      .filter(Boolean)
      .join(' ') || undefined
  const valueSizingClass = compact
    ? 'py-[5px] pl-2.5 text-sm focus:py-[4px] focus:pl-[9px]'
    : 'py-2 pl-3 text-base focus:py-[7px] focus:pl-[11px]'
  const fieldValue =
    props.type === 'multi-select' ||
    props.type === 'email-list' ||
    props.type === 'date-list' ||
    props.type === 'boolean'
      ? ''
      : (props.value ?? '')
  const effectivePlaceholder = props.readOnly ? undefined : props.placeholder
  const labelClass = props.labelClassName ?? (props.error ? 'text-danger' : 'text-muted')
  const inputStateClass = props.error
    ? 'border-danger focus:border-danger'
    : 'border-[color:var(--input-border)] focus:border-blue-500'
  const placeholderClass =
    '[&::placeholder]:font-light [&::placeholder]:italic [&::placeholder]:text-[color:var(--input-placeholder)] [&::placeholder]:[font-synthesis:style]'
  const hasCopyableValue = fieldValue.trim().length > 0
  const formMode = props.formMode ?? 'create'
  const requirementMark = useRequirementMark({
    required: Boolean(props.required),
    // A field someone fills in: not one being edited in place, read only, a
    // toggle (always set), or a control its FieldRow names (the row counts).
    counted:
      formMode === 'create' && !props.readOnly && !props.hideLabel && props.type !== 'boolean',
    override: props.requirementIndicator,
  })
  // A required field that failed says so in red, whatever the form marks.
  const requirementIndicator =
    props.statusIndicator ??
    (props.required && props.error
      ? { label: REQUIREMENT_MARK_LABEL.required, className: 'text-danger-subtle' }
      : requirementMark
        ? { label: REQUIREMENT_MARK_LABEL[requirementMark], className: 'text-muted' }
        : null)

  const singleRequiredSelectOption =
    props.type === 'select' && props.required && props.options.length === 1
      ? props.options[0]
      : null

  useEffect(() => {
    if (props.type !== 'select') return
    if (!singleRequiredSelectOption) return
    if (props.value === singleRequiredSelectOption.value) return
    props.onChange(singleRequiredSelectOption.value)
  }, [props, singleRequiredSelectOption])

  // Native date/time pickers own their trailing edge; a hover copy button
  // there collides with the browser's picker affordance.
  const copyButton =
    hasCopyableValue &&
    props.type !== 'date' &&
    props.type !== 'time' &&
    props.type !== 'datetime-local' ? (
      <IconButton
        label={copied ? 'Copied' : `Copy ${label}`}
        aria-disabled={isCopying || undefined}
        aria-busy={isCopying || undefined}
        className={`flex h-7 w-7 items-center justify-center rounded transition-colors ${
          copied ? 'text-[color:var(--text-info)]' : 'text-icon-quiet hover:text-subtle'
        }`}
        onClick={() => copy(fieldValue)}
      >
        {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
      </IconButton>
    ) : null
  const trailingActions =
    copyButton || props.endActions ? (
      <div
        className={`absolute right-1 top-1/2 z-10 flex h-7 -translate-y-1/2 items-center gap-0.5 ${
          copied || props.endActionsVisible
            ? ''
            : 'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100'
        }`}
      >
        {copyButton}
        {props.endActions}
      </div>
    ) : null
  const shortTextPaddingClass = props.endActions
    ? 'pr-[4.25rem] focus:pr-[67px]'
    : 'pr-10 focus:pr-[39px]'
  const longTextPaddingClass = props.endActions
    ? 'pr-[4.25rem] focus:pr-[67px]'
    : 'pr-10 focus:pr-[39px]'

  // A long text's textarea, resize grips and hover actions. With a footer the
  // border is the whole control's, so the textarea draws none of its own.
  const renderLongText = (longText: LongTextFieldInputProps, compound: boolean) => (
    <>
      <textarea
        ref={longText.controlRef}
        className={`popover-selector-scrollbar block w-full bg-[color:var(--input-bg)] text-default outline-none ${
          compound
            ? `rounded-none border-0 ${
                compact ? 'py-[5px] pl-2.5 text-sm leading-5' : 'py-2 pl-3 text-base leading-6'
              } ${longText.endActions ? 'pr-[4.25rem]' : 'pr-10'}`
            : `field-input-control border transition-colors focus:border-2 ${
                compact
                  ? 'py-[5px] pl-2.5 text-sm leading-5 focus:py-[4px] focus:pl-[9px]'
                  : 'py-2 pl-3 text-base leading-6 focus:py-[7px] focus:pl-[11px]'
              } ${longTextPaddingClass} ${inputStateClass}`
        } ${placeholderClass} ${controlClassName} ${
          longText.resizeMode === 'custom' || longText.resizeMode === 'none'
            ? 'resize-none'
            : 'resize-y'
        }`}
        style={longText.style}
        value={fieldValue}
        placeholder={effectivePlaceholder}
        readOnly={longText.readOnly}
        required={longText.required}
        aria-label={longText.label}
        aria-invalid={longText.error || undefined}
        aria-required={longText.required || undefined}
        aria-describedby={describedBy}
        onChange={(event) => longText.onChange(event.target.value)}
        onBlur={longText.onBlur}
        onFocus={longText.onControlFocus}
        onBeforeInput={longText.onControlBeforeInput}
        onKeyDown={longText.onControlKeyDown}
        onSelect={longText.onControlSelect}
        onPaste={longText.onControlPaste}
      />
      {trailingActions}
      {longText.resizeMode === 'custom' && longText.onResizeStart ? (
        <>
          <div
            className="field-input-resize-handle-left absolute bottom-px left-px z-10 h-4 w-4"
            style={{ cursor: getResizeCursor(longText.resizeAxes ?? 'both', 'left') }}
            aria-hidden="true"
            onMouseDown={(event) => longText.onResizeStart?.(event, 'left')}
          >
            <ResizeGlyph corner="left" />
          </div>
          <div
            className="field-input-resize-handle-right absolute bottom-px right-px z-10 h-4 w-4"
            style={{ cursor: getResizeCursor(longText.resizeAxes ?? 'both', 'right') }}
            aria-hidden="true"
            onMouseDown={(event) => longText.onResizeStart?.(event, 'right')}
          >
            <ResizeGlyph corner="right" />
          </div>
        </>
      ) : null}
    </>
  )

  const inlineBooleanLabel = props.type === 'boolean' && props.inlineLabel
  // A long text with a footer is one box: its label row is the box's top band.
  const labelInControl = props.type === 'long-text' && Boolean(props.controlFooter)
  const controlSpacingClass = props.hideLabel || inlineBooleanLabel || labelInControl ? '' : 'mt-1'

  const labelRow =
    !props.hideLabel && !inlineBooleanLabel ? (
      <div
        className={
          labelInControl
            ? // The label's own inset brings its text in line with the box's padding.
              'flex min-h-8 items-center justify-between gap-2 border-b border-[color:var(--input-border)] bg-lighter py-1 pl-1.5 pr-1'
            : 'flex items-center justify-between'
        }
      >
        <span
          className={`field-input-label inline-flex items-center gap-1 text-xs font-[450] leading-4 ${labelClass}`}
        >
          {label}
          {props.labelTooltip ? (
            <Tooltip content={props.labelTooltip} contentClassName="w-80" clickToggle>
              <button
                type="button"
                className="inline-flex h-4 shrink-0 items-center text-icon-quiet transition-colors hover:text-muted"
                aria-label={`${label} help`}
                aria-describedby={helpId}
              >
                <InfoIcon size={12} />
              </button>
            </Tooltip>
          ) : null}
          {props.labelAccessory}
        </span>
        {props.labelActions || requirementIndicator ? (
          <span className="field-input-requirement inline-flex items-center gap-3">
            {props.labelActions}
            {requirementIndicator ? (
              <span
                className={`inline-flex items-center gap-1 text-xs font-normal italic leading-4 [font-synthesis:style] ${requirementIndicator.className}`}
              >
                {requirementIndicator.icon}
                {requirementIndicator.label}
                {'trailing' in requirementIndicator ? requirementIndicator.trailing : null}
              </span>
            ) : null}
          </span>
        ) : null}
      </div>
    ) : null

  return (
    <div className={`field-input block ${className}`}>
      {labelInControl ? null : labelRow}
      {props.labelTooltip && (
        <div id={helpId} className="sr-only">
          {props.labelTooltip}
        </div>
      )}
      {props.type === 'boolean' ? (
        <div
          className={`field-input-control flex w-full items-center justify-between gap-3 border bg-[color:var(--input-bg)] pr-3 leading-5 font-[450] text-default ${compact ? 'py-[5px] pl-2.5 text-sm' : 'py-2 pl-3 text-base'} ${controlSpacingClass} ${inputStateClass} ${controlClassName}`}
        >
          <span>{inlineBooleanLabel ? label : props.value ? 'Yes' : 'No'}</span>
          <ToggleSwitch
            checked={props.value}
            onChange={props.onChange}
            disabled={props.readOnly}
            ariaLabel={label}
          />
        </div>
      ) : props.type === 'short-text' ||
        props.type === 'date' ||
        props.type === 'time' ||
        props.type === 'datetime-local' ? (
        <div className={`group relative ${controlSpacingClass}`}>
          <input
            ref={props.type === 'short-text' ? textInputRef : undefined}
            type={props.type === 'short-text' ? (props.secret ? 'password' : 'text') : props.type}
            autoComplete={props.type === 'short-text' && props.secret ? 'new-password' : undefined}
            className={`field-input-control w-full border bg-[color:var(--input-bg)] leading-5 font-[450] text-default outline-none transition-colors read-only:bg-lighter read-only:text-data focus:border-2 ${valueSizingClass} ${
              props.type === 'short-text' ? shortTextPaddingClass : 'pr-3'
            } ${inputStateClass} ${placeholderClass} ${controlClassName}`}
            value={fieldValue}
            placeholder={effectivePlaceholder}
            readOnly={props.readOnly}
            required={props.required}
            min={props.type !== 'short-text' ? props.min : undefined}
            max={props.type !== 'short-text' ? props.max : undefined}
            step={props.type !== 'short-text' ? props.step : undefined}
            aria-label={props.label}
            aria-invalid={props.error || undefined}
            aria-describedby={describedBy}
            onChange={(event) => {
              const input = event.target
              const normalize = props.type === 'short-text' ? props.normalize : undefined
              if (!normalize) {
                props.onChange(input.value)
                return
              }
              const cursor = input.selectionStart ?? input.value.length
              setNormalizedCursor({ at: normalize(input.value.slice(0, cursor)).length })
              props.onChange(normalize(input.value))
            }}
            onBlur={props.onBlur}
          />
          {trailingActions}
        </div>
      ) : props.type === 'stepper' ? (
        <div className={`group relative ${controlSpacingClass}`}>
          <div className={`flex w-full items-stretch ${controlClassName}`}>
            <input
              type="text"
              inputMode="decimal"
              // An inset shadow doubles the focus border without a layout shift.
              className={`min-w-0 flex-1 rounded-l-[var(--field-corner-radius)] border font-[450] leading-5 text-default outline-none transition-[color,box-shadow,border-color,background-color] read-only:bg-lighter read-only:text-data focus:border-blue-500 focus:shadow-[inset_0_0_0_1px_theme(colors.blue.500)] ${
                props.error ? 'border-danger' : 'border-[color:var(--input-border)]'
              } ${
                props.error || props.readOnly
                  ? ''
                  : 'hover:border-[color:var(--border-info)] hover:bg-[color:color-mix(in_srgb,var(--surface-info-subtle)_50%,transparent)] focus:hover:bg-[color:var(--input-bg)]'
              } ${props.readOnly ? 'bg-lighter' : 'bg-[color:var(--input-bg)]'} ${
                compact ? 'py-[5px] pl-2.5 text-sm' : 'py-2 pl-3 text-base'
              } ${props.suffix ? 'pr-9' : 'pr-2'} ${placeholderClass}`}
              value={fieldValue}
              placeholder={effectivePlaceholder}
              readOnly={props.readOnly}
              aria-label={props.label}
              aria-invalid={props.error || undefined}
              aria-describedby={describedBy}
              onChange={(event) => props.onChange(event.target.value)}
              onBlur={() => {
                const snapped = snapStepperValue(fieldValue, props)
                if (snapped !== fieldValue) props.onChange(snapped)
                props.onBlur?.()
              }}
              onKeyDown={(event) => {
                if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
                event.preventDefault()
                props.onChange(
                  stepStepperValue(fieldValue, props, event.key === 'ArrowUp' ? 1 : -1)
                )
              }}
            />
            {props.suffix ? (
              <span
                className={`pointer-events-none absolute right-[30px] top-1/2 -translate-y-1/2 text-muted ${
                  compact ? 'text-xs' : 'text-sm'
                }`}
              >
                {props.suffix}
              </span>
            ) : null}
            <div
              className={`flex w-6 shrink-0 flex-col overflow-hidden rounded-r-[var(--field-corner-radius)] border border-l-0 bg-lighter ${
                props.error ? 'border-danger' : 'border-[color:var(--input-border)]'
              }`}
            >
              <button
                type="button"
                tabIndex={-1}
                aria-label={`Increase ${label}`}
                disabled={props.readOnly}
                onClick={() => props.onChange(stepStepperValue(fieldValue, props, 1))}
                className={`flex flex-1 items-center justify-center ${props.readOnly ? 'text-faint' : 'text-icon-quiet'} transition-colors hover:bg-muted hover:text-subtle disabled:cursor-not-allowed`}
              >
                <ChevronDownIcon size={11} className="rotate-180" aria-hidden="true" />
              </button>
              <button
                type="button"
                tabIndex={-1}
                aria-label={`Decrease ${label}`}
                disabled={props.readOnly}
                onClick={() => props.onChange(stepStepperValue(fieldValue, props, -1))}
                className={`flex flex-1 items-center justify-center border-t border-[color:var(--input-border)] ${props.readOnly ? 'text-faint' : 'text-icon-quiet'} transition-colors hover:bg-muted hover:text-subtle disabled:cursor-not-allowed`}
              >
                <ChevronDownIcon size={11} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      ) : props.type === 'select' ? (
        <div className={controlSpacingClass}>
          {singleRequiredSelectOption ? (
            <div className="relative">
              <input
                className={`field-input-control w-full border bg-lighter pr-3 leading-5 font-[450] text-data outline-none ${
                  compact ? 'py-[5px] pl-2.5 text-sm' : 'py-2 pl-3 text-base'
                } ${inputStateClass} ${controlClassName}`}
                value={singleRequiredSelectOption.label}
                readOnly
                aria-label={props.label}
                aria-required={props.required}
              />
            </div>
          ) : (
            <ValueSelector
              fullWidth
              variant="field"
              options={props.options.map(({ value, ...option }) => ({ id: value, ...option }))}
              value={fieldValue}
              onChange={props.onChange}
              placeholder={effectivePlaceholder ?? ''}
              emptyLabel={props.emptyLabel}
              emptyOptionsMessage="No options are available"
              allowEmpty={(props.allowEmpty ?? !props.required) && !props.readOnly}
              clearable={props.clearable}
              showSearch={props.showSearch}
              openRequest={props.openRequest}
              disabled={props.readOnly}
              ariaLabel={props.label}
              ariaInvalid={props.error}
              ariaRequired={props.required}
              ariaDescribedBy={describedBy}
              onBlur={props.onBlur}
              controlClassName={controlClassName}
            />
          )}
        </div>
      ) : props.type === 'multi-select' ? (
        <div className={controlSpacingClass}>
          <MultiValueSelector
            options={props.options.map(({ value, ...option }) => ({ ...option, id: value }))}
            values={props.value}
            onChange={props.onChange}
            placeholder={effectivePlaceholder ?? ''}
            showSearch={props.showSearch}
            disabled={props.readOnly}
            ariaLabel={props.label}
            ariaInvalid={props.error}
            ariaRequired={props.required}
            ariaDescribedBy={describedBy}
            onBlur={props.onBlur}
            controlClassName={controlClassName}
            chipRemoveLabel={props.chipRemoveLabel}
            chipTitle={props.chipTitle}
            onOpen={props.onOpen}
            ordered={props.ordered}
          />
        </div>
      ) : props.type === 'email-list' ? (
        <div className={controlSpacingClass}>
          <EmailListInput
            values={props.value}
            onChange={props.onChange}
            onPendingChange={props.onPendingChange}
            placeholder={effectivePlaceholder}
            disabled={props.readOnly}
            ariaLabel={props.label}
            ariaInvalid={props.error}
            ariaRequired={props.required}
            onBlur={props.onBlur}
            className={controlClassName}
          />
        </div>
      ) : props.type === 'date-list' ? (
        <div className={controlSpacingClass}>
          <DateListInput
            values={props.value}
            onChange={props.onChange}
            disabled={props.readOnly}
            ariaLabel={props.label}
            ariaInvalid={props.error}
            ariaRequired={props.required}
            onBlur={props.onBlur}
            className={controlClassName}
          />
        </div>
      ) : props.type === 'long-text' ? (
        props.controlFooter ? (
          <div
            className={`field-input-control overflow-hidden border bg-[color:var(--input-bg)] transition-[border-color,box-shadow] ${controlSpacingClass} ${
              props.error
                ? 'border-danger'
                : 'border-[color:var(--input-border)] focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500'
            }`}
          >
            {labelRow}
            {props.controlHeader}
            <div className="group relative block w-full">{renderLongText(props, true)}</div>
            {props.controlFooter}
          </div>
        ) : (
          <div className={`group relative block w-full ${controlSpacingClass}`}>
            {renderLongText(props, false)}
          </div>
        )
      ) : null}
      {showsError ? (
        <p id={errorId} className="field-input-label mt-1 text-xs text-danger">
          {props.errorMessage}
        </p>
      ) : null}
      {copyError && (
        <p id={copyErrorId} role="alert" className="mt-1 text-xs text-danger">
          {copyError}
        </p>
      )}
    </div>
  )
}

export function InternalMetadataField({
  label,
  value,
  tooltip,
  className = '',
}: InternalMetadataFieldProps) {
  const { copied, copyError, isCopying, copy } = useClipboardCopy()
  const fieldValue = value ?? ''
  const hasCopyableValue = fieldValue.trim().length > 0

  return (
    <div
      className={`field-input flex min-w-0 flex-wrap items-center gap-1 text-xs leading-4 ${className}`}
    >
      <span className="field-input-label shrink-0 font-medium text-muted">{label}</span>
      <Tooltip content={tooltip} contentClassName="w-80">
        <button
          type="button"
          className="inline-flex shrink-0 items-center text-icon-quiet transition-colors hover:text-data"
          aria-label={`${label} help`}
        >
          <InfoIcon size={12} />
        </button>
      </Tooltip>
      <span className="shrink-0 text-muted">:</span>
      <span className="min-w-0 truncate font-mono text-[11px] text-data">{fieldValue}</span>
      {hasCopyableValue ? (
        <IconButton
          label={copied ? 'Copied' : `Copy ${label}`}
          aria-disabled={isCopying || undefined}
          aria-busy={isCopying || undefined}
          wrapperClassName="shrink-0"
          className={`inline-flex h-4 w-4 items-center justify-center rounded transition-colors ${
            copied
              ? 'text-[color:var(--text-info)]'
              : 'text-icon-quiet hover:bg-muted hover:text-subtle'
          }`}
          onClick={() => copy(fieldValue)}
        >
          {copied ? <CheckIcon size={12} /> : <CopyIcon size={12} />}
        </IconButton>
      ) : null}
      {copyError && (
        <p role="alert" className="basis-full text-xs text-danger">
          {copyError}
        </p>
      )}
    </div>
  )
}
